import React, { useCallback, useEffect, useMemo, useState } from "react"
import { AnimatePresence, motion } from "framer-motion"
import { listCryptoLogos } from "@/modules/crypto-assets/services/cryptoAssetsService"

type LogoItem = { id: string; symbol: string; base_asset?: string; logo_url: string }

function useResponsiveColumns() {
  const [cols, setCols] = useState(3)
  useEffect(() => {
    const mqlSm = window.matchMedia("(max-width: 640px)")
    const mqlMd = window.matchMedia("(min-width: 641px) and (max-width: 1024px)")
    const update = () => {
      if (mqlSm.matches) setCols(2)
      else if (mqlMd.matches) setCols(3)
      else setCols(4)
    }
    update()
    mqlSm.addEventListener("change", update)
    mqlMd.addEventListener("change", update)
    return () => {
      mqlSm.removeEventListener("change", update)
      mqlMd.removeEventListener("change", update)
    }
  }, [])
  return cols
}

const shuffleArray = <T,>(array: T[]): T[] => {
  const shuffled = [...array]
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1))
    ;[shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]]
  }
  return shuffled
}

function distributeLogos(all: LogoItem[], columnCount: number): LogoItem[][] {
  const shuffled = shuffleArray(all)
  const cols: LogoItem[][] = Array.from({ length: columnCount }, () => [])
  shuffled.forEach((logo, i) => cols[i % columnCount].push(logo))

  const maxLen = Math.max(1, ...cols.map((c) => c.length))
  cols.forEach((c) => {
    while (c.length < maxLen) c.push(shuffled[Math.floor(Math.random() * shuffled.length)])
  })
  return cols
}

function normalizeUrl(url?: string) {
  if (!url) return ""
  if (/^(https?:)?\/\//i.test(url) || url.startsWith("data:")) return url
  return url.startsWith("/") ? url : `/${url}`
}

const QUOTE_SUFFIXES = ["USDT", "USD", "USDC", "FDUSD", "DAI", "BUSD", "TUSD", "TRY", "EUR", "BRL", "BTC", "ETH", "BNB"]
function guessLocalLogo(symbol: string, baseAsset?: string) {
  const base = (baseAsset || symbol || "").toUpperCase()
  let token = base
  if (!baseAsset) {
    for (const q of QUOTE_SUFFIXES) {
      if (token.endsWith(q)) {
        token = token.slice(0, -q.length)
        break
      }
    }
  }
  token = token.replace(/[^A-Z0-9]/g, "").toLowerCase()
  return `/${token}.png`
}

function LogoTile({ item }: { item: LogoItem }) {
  const [src, setSrc] = useState(() => normalizeUrl(item.logo_url))
  const [failedOnce, setFailedOnce] = useState(false)
  const onError = useCallback(() => {
    if (!failedOnce) {
      setFailedOnce(true)
      setSrc(guessLocalLogo(item.symbol, item.base_asset))
    } else {
      setSrc("")
    }
  }, [failedOnce, item.symbol, item.base_asset])

  const token = (item.base_asset || item.symbol || "?").replace(/[^A-Z0-9]/gi, "").slice(0, 4).toUpperCase()

  const circleClasses =
    "flex h-20 w-20 items-center justify-center rounded-full border border-transparent bg-muted/20 overflow-hidden md:h-24 md:w-24"

  if (!src) {
    return (
      <div className={circleClasses}>
        <span className="text-[0.75rem] text-foreground/70">{token}</span>
      </div>
    )
  }

  return (
    <div className={circleClasses}>
      <img
        src={src}
        alt={item.symbol}
        onError={onError}
        className="h-14 w-14 rounded-full object-contain md:h-16 md:w-16"
        loading="lazy"
        referrerPolicy="no-referrer"
      />
    </div>
  )
}

function LogoColumn({ logos, index, currentTime }: { logos: LogoItem[]; index: number; currentTime: number }) {
  const cycleInterval = 2000
  const columnDelay = index * 200
  const adjustedTime = (currentTime + columnDelay) % (cycleInterval * logos.length)
  const currentIndex = Math.floor(adjustedTime / cycleInterval)
  const currentLogo = useMemo(() => logos[currentIndex], [logos, currentIndex])

  return (
    <motion.div
      className="relative h-28 w-28 overflow-hidden rounded-md bg-background/40 md:h-32 md:w-32"
      initial={{ opacity: 0, y: 50 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.1, duration: 0.5, ease: "easeOut" }}
    >
      <AnimatePresence mode="wait">
        <motion.div
          key={`${currentLogo.id}-${currentIndex}`}
          className="absolute inset-0 flex items-center justify-center"
          initial={{ y: "10%", opacity: 0, filter: "blur(8px)" }}
          animate={{
            y: "0%",
            opacity: 1,
            filter: "blur(0px)",
            transition: { type: "spring", stiffness: 300, damping: 20, mass: 1, bounce: 0.2, duration: 0.5 },
          }}
          exit={{ y: "-20%", opacity: 0, filter: "blur(6px)", transition: { type: "tween", ease: "easeIn", duration: 0.3 } }}
        >
          <LogoTile item={currentLogo} />
        </motion.div>
      </AnimatePresence>
    </motion.div>
  )
}

export default function CoinsLogoCarousel() {
  const [logos, setLogos] = useState<LogoItem[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [currentTime, setCurrentTime] = useState(0)
  const columnCount = useResponsiveColumns()

  const updateTime = useCallback(() => setCurrentTime((t) => t + 100), [])

  useEffect(() => {
    const id = window.setInterval(updateTime, 100)
    return () => window.clearInterval(id)
  }, [updateTime])

  useEffect(() => {
    let mounted = true
    ;(async () => {
      try {
        setLoading(true)
        const data = await listCryptoLogos(48)
        if (mounted) setLogos(data)
      } catch (e: any) {
        if (mounted) setError(e?.message || "Falha ao carregar logos")
      } finally {
        if (mounted) setLoading(false)
      }
    })()
    return () => {
      mounted = false
    }
  }, [])

  const sets = useMemo(() => (logos.length ? distributeLogos(logos, columnCount) : []), [logos, columnCount])

  return (
    <section id="monitoramento" className="relative pt-40 pb-40">
      <div className="mx-auto max-w-[1550px] px-6">
        <div className="text-center">
          <div className="text-sm text-foreground/70">
            {"Cobertura ampla de pares e ativos acompanhados 24/7 para alertas e cenários técnicos."}
          </div>
          <h2 className="mt-1 text-2xl font-semibold sm:text-3xl">
            {"Centenas de ativos monitorados em tempo real"}
          </h2>
        </div>

        {error && (
          <div className="mx-auto mt-6 max-w-xl rounded-md border border-destructive/30 bg-destructive/10 p-2 text-sm text-destructive-foreground">
            {error}
          </div>
        )}

        <div className="mt-12 md:mt-16">
          {loading ? (
            <div className="text-center text-sm text-foreground/70">{"Carregando logos..."}</div>
          ) : logos.length === 0 ? (
            <div className="text-center text-sm text-foreground/60">{"Nenhum logo enviado ainda."}</div>
          ) : (
            <div className="mx-auto flex max-w-5xl items-stretch justify-center gap-6 md:gap-8">
              {sets.map((col, idx) => (
                <LogoColumn key={idx} logos={col} index={idx} currentTime={currentTime} />
              ))}
            </div>
          )}
        </div>

        <p className="mt-6 mx-auto max-w-3xl text-center text-[0.7rem] text-foreground/60 italic font-light">
          {
            "A inclusão de um ativo na cobertura não constitui recomendação de investimento, compra ou venda. A lista reflete ativos disponíveis/monitorados em mercados e pode mudar sem aviso."
          }
        </p>
      </div>
      <div
        className="pointer-events-none absolute bottom-0 left-0 h-px w-full"
        style={{
          background:
            "radial-gradient(50% 50% at 50% 50%, rgba(255,255,255,0.24) 0%, rgba(255,255,255,0) 100%)",
        }}
      />
    </section>
  )
}
